import { useCallback, useState } from 'react';
import FileSaver from 'file-saver';
import { buildNovaDocx, buildNovaPdf, htmlToExportBlocks, htmlToPlainText, sanitizeExportFileName } from './novaExport';

type NovaExportFormat = 'word' | 'pdf';

// Shared response-actions state/handlers for a Nova answer — used identically by
// NovaAnswerCard (full page) and NovaMessage (floating widget) so the two surfaces
// can't drift out of sync on copy/export behaviour the way two hand-duplicated
// copies of this logic eventually would.
export const useNovaResponseActions = (htmlForCopy: string) => {
  const [copied, setCopied] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [exportToast, setExportToast] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [exporting, setExporting] = useState(false);

  // The "Copied with formatting" toast means what it says: writes both a rich HTML
  // flavour (so pasting into email/Word/Slack keeps headings and bold) and a plain
  // text fallback, mirroring the ClipboardItem pattern already used by StylusAI's
  // own copy button (modules/AIchatIntegration2.tsx) — falling back to plain text
  // if the browser doesn't support multi-format clipboard writes.
  const copyToClipboard = useCallback(() => {
    // Plain flavour built from the same HTML, so links appear as "text (url)" rather
    // than being lost — it's what a plain-text paste target (or a non-rich editor) gets.
    const plainText = htmlToPlainText(htmlForCopy);
    const writeRich = () =>
      navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([htmlForCopy], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        }),
      ]);

    (typeof ClipboardItem !== 'undefined' ? writeRich() : navigator.clipboard.writeText(plainText)).catch(() =>
      navigator.clipboard.writeText(plainText),
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }, [htmlForCopy]);

  // No document-generation backend exists for Nova, so the file is built client-side
  // from the same rendered HTML the answer/copy button already uses — headings, lists
  // and tables become real Word/PDF structure rather than a flat text dump.
  const handleExport = useCallback(
    async (name: string, format: NovaExportFormat) => {
      if (exporting) return;
      const fileName = sanitizeExportFileName(name);
      const blocks = htmlToExportBlocks(htmlForCopy);

      setExporting(true);
      try {
        if (format === 'word') {
          FileSaver.saveAs(await buildNovaDocx(fileName, blocks), `${fileName}.docx`);
        } else {
          FileSaver.saveAs(await buildNovaPdf(fileName, blocks), `${fileName}.pdf`);
        }
        setExportToast(true);
        setTimeout(() => setExportToast(false), 2500);
      } catch (error) {
        console.error('Nova export failed', error);
        // A thrown export otherwise fails completely silently — the dialog closes
        // and nothing downloads, with no sign anything went wrong.
        setExportError(true);
        setTimeout(() => setExportError(false), 3500);
      } finally {
        setExporting(false);
      }
    },
    [htmlForCopy, exporting],
  );

  return {
    copied,
    infoOpen,
    setInfoOpen,
    downloadOpen,
    setDownloadOpen,
    exportToast,
    exportError,
    exporting,
    copyToClipboard,
    handleExport,
  };
};
